// filepath: /Users/a001/code/pk-h5-web/src/views/login/index.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Toast } from 'antd-mobile';
import { createNonceStr, md5, getLocationSearch } from '@/utils/utils';
import request from '@/utils/request';
import config from '@/config';
import { useTranslation } from '@/i18n';
import './index.scss';

const Login = () => {
  const [mobilePhone, setMobilePhone] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [areaCode, setAreaCode] = useState('+86');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMode, setLoginMode] = useState('code'); // 'password', 'code', 'register'
  const [sendingCode, setSendingCode] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const history = useHistory();
  const location = useLocation();
  const t = useTranslation();
  const dropdownRef = useRef();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const areaCodes = [
    { code: '+86', name: t('country.china') },
    { code: '+1', name: t('country.usa') },
    { code: '+44', name: t('country.uk') },
    { code: '+91', name: t('country.india') },
    { code: '+65', name: t('country.singapore') },
    { code: '+852', name: t('country.hongkong') },
    { code: '+886', name: t('country.taiwan') },
    { code: '+81', name: t('country.japan') },
    { code: '+82', name: t('country.korea') },
    { code: '+60', name: t('country.malaysia') },
    { code: '+66', name: t('country.thailand') },
    { code: '+84', name: t('country.vietnam') },
    { code: '+62', name: t('country.indonesia') },
    { code: '+63', name: t('country.philippines') },
    { code: '+7', name: t('country.russia') },
    { code: '+49', name: t('country.germany') },
    { code: '+33', name: t('country.france') },
    { code: '+39', name: t('country.italy') },
    { code: '+34', name: t('country.spain') },
    { code: '+31', name: t('country.netherlands') },
    { code: '+46', name: t('country.sweden') },
    { code: '+47', name: t('country.norway') },
    { code: '+45', name: t('country.denmark') },
    { code: '+55', name: t('country.brazil') },
    { code: '+52', name: t('country.mexico') },
    { code: '+61', name: t('country.australia') },
    { code: '+64', name: t('country.newzealand') },
    { code: '+966', name: t('country.saudiarabia') },
    { code: '+971', name: t('country.uae') },
    { code: '+974', name: t('country.qatar') },
    { code: '+965', name: t('country.kuwait') },
    { code: '+973', name: t('country.bahrain') },
    { code: '+968', name: t('country.oman') },
  ];

  useEffect(() => {
    const title = loginMode === 'register' ? t('login.register') : t('login.title');
    document.title = title;
    // 设置页面标题（如果在 WebView 中）
    if (typeof window?.savePicture?.setTitle === 'function') {
      window?.savePicture?.setTitle(title);
    }
  }, [loginMode, t]);

  // 获取签名参数
  const getSignParams = () => {
    const appKey = 'app_h5';
    const secret = '11dc72cf-88b0-46cd-91d7-b1a77fd69ee1';
    const timestamp = new Date().getTime();
    const random = createNonceStr();
    const tempStr = `appKey${appKey}secret${secret}timestamp${timestamp}random${random}`;
    console.log('tempStr:', tempStr);
    const sign = md5(tempStr, 32).toString(16).toLocaleUpperCase();
    console.log('sign:', sign);
    return {
      appKey,
      timestamp,
      random,
      sign,
    };
  };

  // 验证手机号格式
  const validatePhone = (phone) => {
    const phoneRegex = /^1[3-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  // 验证密码格式
  const validatePassword = (pwd) => {
    return pwd.length >= 6;
  };

  // 发送验证码
  const sendVerificationCode = async () => {
    if (!mobilePhone) {
      Toast.fail(t('login.phoneRequired'), 2);
      return;
    }
    setSendingCode(true);
    try {
      const signParams = getSignParams();
      const response = await request.get(`/api/driverUser/checkCode/${mobilePhone}?type=1&areaCode=${encodeURIComponent(areaCode)}`, {}, signParams);
      if (response && response.data && response.data.errorCode === 0) {
        Toast.success(t('login.codeSent'), 2);
        setCountdown(60);
      } else {
        Toast.fail(response?.data?.errorMsg || t('login.sendCodeFailed'), 2);
      }
    } catch (error) {
      console.error('发送验证码失败', error);
      Toast.fail(t('login.networkError'), 2);
    } finally {
      setSendingCode(false);
    }
  };

  // 处理提交（登录或注册）
  const handleSubmit = async () => {
    if (!mobilePhone) {
      Toast.fail(t('login.phoneRequired'), 2);
      return;
    }

    if (loginMode === 'password' || loginMode === 'register') {
      if (!password) {
        Toast.fail(t('login.passwordRequired'), 2);
        return;
      }
      if (!validatePassword(password)) {
        Toast.fail(t('login.passwordMinLength'), 2);
        return;
      }
    } else if (loginMode === 'code') {
      if (!verificationCode) {
        Toast.fail(t('login.codeRequired'), 2);
        return;
      }
    }

    setLoading(true);
    Toast.loading(loginMode === 'register' ? t('login.registering') : t('login.loggingIn'), 0, () => {}, true);

    try {
      const signParams = getSignParams();
      let requestData, apiUrl;

      if (loginMode === 'password') {
        apiUrl = '/api/driverUser/passwordLogin';
        requestData = {
          loginType: 2,
          mobilePhone: mobilePhone,
          type: 1,
          driverPassword: password,
          appKey: 'invitation_link',
          areaCode: areaCode,
        };
      } else if (loginMode === 'code') {
        apiUrl = '/api/driverUser/login';
        requestData = {
          loginType: 1,
          mobilePhone: mobilePhone,
          checkCode: verificationCode,
          type: 1,
          appKey: 'invitation_link',
          areaCode: areaCode,
        };
      } else if (loginMode === 'register') {
        apiUrl = '/api/driverUser/registerByPwd';
        requestData = {
          driverPassword: password,
          loginType: 2,
          mobilePhone: mobilePhone,
          appKey: 'weChat',
          areaCode: areaCode,
        };
      }

      const response = await request.post(apiUrl, requestData, signParams);

      Toast.hide();
      
      if (response && response.data && response.data.errorCode === 0) {
        const { data } = response.data;
        const { token, driverDetail } = data;
        
        Toast.success(loginMode === 'register' ? t('login.registerSuccess') : t('login.loginSuccess'), 1);
        
        if (token) {
          localStorage.setItem('userToken', token);
        }
        if (driverDetail) {
          localStorage.setItem('driverDetail', JSON.stringify(driverDetail));
        }
        
        // 获取 redirect 参数，如果有则跳转到该页面，否则跳转到首页
        const urlParams = new URLSearchParams(location.search);
        const redirectUrl = urlParams.get('redirect');
        
        setTimeout(() => {
          if (redirectUrl) {
            history.push(decodeURIComponent(redirectUrl));
          } else {
            history.push('/home');
          }
        }, 1000);
      } else {
        const errorMsg = response?.data?.errorMsg || (loginMode === 'register' ? t('login.registerFailed') : t('login.loginFailed'));
        Toast.fail(errorMsg, 2);
      }
    } catch (error) {
      console.error('请求失败', error);
      Toast.hide();
      Toast.fail(t('login.networkError'), 2);
    } finally {
      setLoading(false);
    }
  };

  // 处理键盘回车事件
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="login-container">
      <div className="login-header">
        <div className="logo-wrapper">
          <img src={config.LOGO_IMG} alt="logo" className="logo" />
          <h1 className="app-title">{config.TITLE}</h1>
        </div>
      </div>

      <div className="login-form">
        <div className="form-title">
          <h2>
            {loginMode === 'register' ? t('login.passwordRegister') : loginMode === 'code' ? t('login.codeLogin') : t('login.passwordLogin')}
          </h2>
          <p>{loginMode === 'register' ? t('login.registerHint') : loginMode === 'code' ? t('login.codeLoginHint') : t('login.loginHint')}</p>
        </div>

        <div className="input-group">
          <div className="input-wrapper">
            <div className="input-icon">
              <i className="iconfont icon-phone"></i>
            </div>
            <div ref={dropdownRef} className="area-code-select" onClick={(e) => { e.stopPropagation(); setIsDropdownOpen(!isDropdownOpen); }}>
              {areaCode}
              <span className="dropdown-arrow">{isDropdownOpen ? '▲' : '▼'}</span>
            </div>
            {isDropdownOpen && (
              <ul className="dropdown-list">
                {areaCodes.map(item => (
                  <li key={item.code} onClick={() => { console.log('Selected area code:', item.code); setAreaCode(item.code); setIsDropdownOpen(false); }}>
                    {item.name} {item.code}
                  </li>
                ))}
              </ul>
            )}
            <input
              type="tel"
              placeholder={t('login.phonePlaceholder')}
              value={mobilePhone}
              onChange={(e) => setMobilePhone(e.target.value.replace(/[^\d]/g, '').slice(0, 11))}
              onKeyPress={handleKeyPress}
              maxLength="11"
              className="form-input"
            />
          </div>
        </div>

        {(loginMode === 'password' || loginMode === 'register') && (
          <div className="input-group">
            <div className="input-wrapper">
              <div className="input-icon">
                <i className="iconfont icon-lock"></i>
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t('login.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                className="form-input"
              />
              <div 
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                <i className={`iconfont ${showPassword ? 'icon-eye-open' : 'icon-eye-close'}`}></i>
              </div>
            </div>
          </div>
        )}

        {loginMode === 'code' && (
          <div className="input-group">
            <div className="input-wrapper">
              <div className="input-icon">
                <i className="iconfont icon-code"></i>
              </div>
              <input
                type="text"
                placeholder={t('login.codePlaceholder')}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                onKeyPress={handleKeyPress}
                maxLength="6"
                className="form-input"
              />
              <button
                className="send-code-btn"
                onClick={sendVerificationCode}
                disabled={sendingCode || countdown > 0}
              >
                {sendingCode ? t('login.sending') : countdown > 0 ? `${countdown}秒后重发` : t('login.sendCode')}
              </button>
            </div>
          </div>
        )}

        <button
          className={`login-btn ${loading ? 'loading' : ''}`}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (loginMode === 'register' ? t('login.registering') : t('login.loggingIn')) : (loginMode === 'register' ? t('login.registerBtn') : t('login.loginBtn'))}
        </button>

        <div className="mode-switch">
          <p>
            {loginMode === 'register' ? t('login.hasAccount') : loginMode === 'code' ? t('login.usePassword') : t('login.noAccount')}
            <span 
              className="switch-link" 
              onClick={() => setLoginMode(loginMode === 'register' ? 'password' : loginMode === 'code' ? 'password' : loginMode === 'password' ? 'code' : 'register')}
            >
              {loginMode === 'register' ? t('login.goLogin') : loginMode === 'code' ? t('login.goPassword') : loginMode === 'password' ? t('login.goCode') : t('login.goRegister')}
            </span>
          </p>
        </div>

        <div className="login-tips">
          <p>{loginMode === 'register' ? t('login.registerAgreement') : t('login.loginAgreement')}</p>
        </div>
      </div>

      <div className="login-footer">
        <p>{t('login.contactService', { tel: config.TEL })}</p>
      </div>
    </div>
  );
};

export default Login;